/**
 * Lightweight, dependency-free SQL syntax highlighter. Tokenizes with a single
 * regex and colours keywords, types, functions, strings, numbers, comments and
 * punctuation. Intended for the frozen spine SQL shown in the mapping modal —
 * a controlled input, so a regex tokenizer is sufficient (not a full parser).
 */

import React from "react";

const KEYWORDS = new Set([
	"SELECT", "FROM", "WHERE", "JOIN", "LEFT", "RIGHT", "INNER", "OUTER",
	"FULL", "CROSS", "ON", "USING", "AS", "AND", "OR", "NOT", "IN", "IS",
	"NULL", "GROUP", "BY", "ORDER", "HAVING", "LIMIT", "OFFSET", "UNION",
	"ALL", "DISTINCT", "CASE", "WHEN", "THEN", "ELSE", "END", "WITH",
	"CREATE", "TABLE", "VIEW", "TEMP", "TEMPORARY", "INSERT", "INTO",
	"VALUES", "UPDATE", "SET", "DELETE", "OVER", "PARTITION", "ASC", "DESC",
	"BETWEEN", "LIKE", "ILIKE", "EXISTS", "QUALIFY", "WINDOW", "FILTER",
	"NULLS", "FIRST", "LAST", "ROWS", "RANGE", "UNBOUNDED", "PRECEDING",
	"FOLLOWING", "CURRENT", "ROW", "PIVOT", "UNPIVOT", "EXCLUDE", "REPLACE",
]);

const TYPES = new Set([
	"INTEGER", "INT", "BIGINT", "SMALLINT", "TINYINT", "VARCHAR", "TEXT",
	"CHAR", "DATE", "TIMESTAMP", "TIME", "DOUBLE", "FLOAT", "REAL", "DECIMAL",
	"NUMERIC", "BOOLEAN", "BOOL", "BLOB", "JSON", "UUID", "INTERVAL",
]);

// Groups: 1 comment · 2 single-quote string · 3 double-quote ident/string ·
// 4 number · 5 word · 6 whitespace · 7 punctuation.
const TOKEN_RE =
	/(--[^\n]*|\/\*[\s\S]*?\*\/)|('(?:[^']|'')*')|("(?:[^"]|"")*")|(\b\d+(?:\.\d+)?\b)|([A-Za-z_][A-Za-z0-9_]*)|(\s+)|([^\w\s])/g;

// Light-theme palette (readable on a near-white background).
const CLS = {
	comment: "text-slate-400 italic",
	string: "text-[#0a7d33]",
	number: "text-[#b45309]",
	keyword: "text-[#7c3aed] font-medium",
	type: "text-[#0e7490]",
	func: "text-[#2563eb]",
	punct: "text-slate-500",
	ident: "text-slate-800",
} as const;

/**
 * Normalize the frozen spine's column-alignment padding: keep each line's
 * leading indentation but collapse internal runs of 2+ spaces to one, so
 * ``expr           AS alias`` reads as ``expr AS alias``.
 */
const formatSql = (sql: string): string =>
	sql
		.replace(/\r\n/g, "\n")
		.split("\n")
		.map((line) => {
			const indent = line.match(/^\s*/)?.[0] ?? "";
			return indent + line.slice(indent.length).replace(/ {2,}/g, " ").trimEnd();
		})
		.join("\n");

interface SqlHighlightProps {
	sql: string;
}

const SqlHighlight: React.FC<SqlHighlightProps> = ({ sql: rawSql }) => {
	const sql = formatSql(rawSql);
	const nodes: React.ReactNode[] = [];
	const re = new RegExp(TOKEN_RE);
	let match: RegExpExecArray | null;
	let key = 0;

	while ((match = re.exec(sql)) !== null) {
		const [full, comment, sStr, dStr, num, word, ws, punct] = match;
		if (comment) {
			nodes.push(<span key={key++} className={CLS.comment}>{full}</span>);
		} else if (sStr || dStr) {
			nodes.push(<span key={key++} className={CLS.string}>{full}</span>);
		} else if (num) {
			nodes.push(<span key={key++} className={CLS.number}>{full}</span>);
		} else if (word) {
			const up = word.toUpperCase();
			// A function name is a word immediately followed by "(".
			const isFunc = /^\s*\(/.test(sql.slice(re.lastIndex));
			const cls = KEYWORDS.has(up)
				? CLS.keyword
				: TYPES.has(up)
					? CLS.type
					: isFunc
						? CLS.func
						: CLS.ident;
			nodes.push(<span key={key++} className={cls}>{word}</span>);
		} else if (ws) {
			nodes.push(ws);
		} else if (punct) {
			nodes.push(<span key={key++} className={CLS.punct}>{full}</span>);
		}
	}

	// Own the surrounding <pre> so the light background and token colours always
	// live in one file (a partial hot-reload can't leave dark text on a stale
	// dark background). The default text-slate-800 keeps any uncoloured token
	// readable on the light card.
	return (
		<pre className="overflow-auto rounded-lg border border-gray-200 bg-[#FCFCFD] p-4 text-[13px] leading-[1.7] [tab-size:2]">
			<code className="font-code whitespace-pre text-slate-800">{nodes}</code>
		</pre>
	);
};

export default SqlHighlight;
